import './WaterlineManagement.css'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Card, Link, useTheme } from '@mui/material'
import WaterlineTestAlerts from './WaterlineTestAlerts'
import WaterlineExportButton from './WaterlineExportButton'
import { FilterOption, WaterlineCalendarRow, WaterlineChild, WaterlineCustomer } from './types'
import { getFilteredPractices, getCalendarData } from './utils/helpers'
import { selectCustomerNumber, selectGroups } from '../../../redux/customer'
import { getCorpWaterlineCustomer } from './services'
import { useAppSelector } from '../../../redux/store'
import { selectCurrentUserCustomers } from '../../../redux/user'
import { Groups, TestCompliance } from '../../../types'
import CorpCalendarFilters from '../../../components/CorpCalendarFilters'
import CorpCalendarTable from '../../../components/CorpCalendarTable'
import WaterlineResultsCell from './WaterlineCalendarResultsCell'
import YearHeader from './YearHeader'
import QuarterHeader from './QuarterHeader'
import { BioCalendarRow } from '../biological-monitoring/types'
import { useNavigate } from 'react-router-dom'

const filterOptions: FilterOption[] = [
  { label: 'All Results', value: 'All results' },
  { label: 'Failed Retests', value: TestCompliance.failedRetest },
  { label: 'Failed Tests', value: TestCompliance.failedTest },
  { label: 'Passed Retests', value: TestCompliance.passedRetest },
  { label: 'Passed Tests', value: TestCompliance.passedTest },
  { label: 'Missed Tests', value: TestCompliance.missedTest },
]

const WaterlineCalendar = () => {
  const [search, setSearch] = useState('')
  const [filterByType, setFilterByType] = useState('')
  const [filterByGroup, setFilterByGroup] = useState<Groups[]>([])

  const [corpChildren, setCorpChildren] = useState<WaterlineChild[] | null>(null)
  const [practices, setPractices] = useState<WaterlineCalendarRow[] | null>(null)

  const customerNumber = useSelector(selectCustomerNumber)
  const groups = useSelector(selectGroups)
  const groupCustomerNumbers = useAppSelector(selectCurrentUserCustomers)

  useEffect(() => {
    if (customerNumber && !corpChildren && groupCustomerNumbers) {
      (async () => {
        try {
          const children = await getCorpWaterlineCustomer(customerNumber, groupCustomerNumbers)
          setCorpChildren(children)
          const data = getCalendarData(children)
          setPractices(data)
        } catch (err) {
          console.error(err)
        }
      })()
    }
  }, [customerNumber, groupCustomerNumbers])

  const filteredPractices = useMemo(() => {
    if (search || filterByType || filterByGroup?.length) {
      const childPractices = structuredClone(practices) ?? []
      const filtered = getFilteredPractices(search, filterByType, filterByGroup, childPractices)
      return filtered
    } else {
      return practices ?? []
    }
  }, [practices, filterByType, filterByGroup, search])

  const isLoading = useMemo(() => {
    return !(practices && customerNumber)
  }, [practices, customerNumber])

  const PracticeLink = ({ practice }: { practice: BioCalendarRow | WaterlineCalendarRow }) => {
    const theme = useTheme()
    const navigate = useNavigate()
    return (
      <Link component="button" onClick={() => navigate(`/corp/practice/${practice.customerNumber}/waterline-management`)} sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }}>
        {practice.practiceName}
      </Link>
    )
  }

  return (
    <Card sx={{ width: "100%" }} variant="outlined">
      <Box className="box-header">
        {filteredPractices.length} Practice{filteredPractices.length === 1 ? '' : 's'}
      </Box>
      <Box className="table-subheader flex flex-space-between">
        <CorpCalendarFilters
          groups={groups}
          filterByType={filterByType}
          filterByGroup={filterByGroup}
          setFilterByType={setFilterByType}
          setFilterByGroup={setFilterByGroup}
          setSearch={setSearch}
          filterOptions={filterOptions}
        />
      </Box>
      <Box className="table-subheader flex-space-between">
        <Box className="flex">
          <div className="background-danger test-label bold">Failure</div>
          <div className="background-warning test-label bold">Missed/Retest</div>
          <div className="background-success test-label bold">Passed</div>
          <div className="background-inactive test-label bold">Not Started</div>
        </Box>
        <WaterlineExportButton corpChildren={corpChildren} />
      </Box>
      <Box>
        <WaterlineTestAlerts practices={practices} />
      </Box>
      <Box className="waterline-table-wrapper">
        <CorpCalendarTable
          practices={filteredPractices}
          isLoading={isLoading}
          testResultsCell={WaterlineResultsCell}
          yearHeader={<YearHeader />}
          quarterHeader={<QuarterHeader />}
          practiceLink={PracticeLink}
        />
      </Box>
    </Card>
  )
}

export default WaterlineCalendar
